import React, { useState, useEffect, useMemo } from 'react';
import { TagIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

const TagsManager = ({ onTagSelect, selectedTags = [] }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagSearch, setTagSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadAllTags() {
      try {
        const collected = [];
        let nextToken = null;
        do {
          const result = await bookmarkService.getTags({ limit: 200, nextToken });
          collected.push(...result.items);
          nextToken = result.nextToken || null;
        } while (nextToken && !cancelled);

        if (!cancelled) {
          const sorted = [...collected].sort((a, b) => (b.count || 0) - (a.count || 0));
          setTags(sorted);
        }
      } catch (err) {
        console.error('Error loading tags:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAllTags();
    return () => { cancelled = true; };
  }, []);

  const { selectedSet, visibleTags } = useMemo(() => {
    const selectedSet = new Set(selectedTags);
    const q = tagSearch.trim().toLowerCase();
    const matches = q
      ? tags.filter(t => t.tagName.toLowerCase().includes(q))
      : tags;
    // Selected tags always appear at the top, even if they don't match the search
    const selectedInList = tags.filter(t => selectedSet.has(t.tagName) && !matches.includes(t));
    return { selectedSet, visibleTags: [...selectedInList, ...matches] };
  }, [tags, tagSearch, selectedTags]);

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <TagIcon className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-500">Loading tags...</span>
      </div>
    );
  }

  if (tags.length === 0) {
    return (
      <div className="flex items-center space-x-2">
        <TagIcon className="h-5 w-5 text-gray-400" />
        <span className="text-sm text-gray-500">No tags yet</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <TagIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tags</span>
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500">{tags.length} total</span>
      </div>

      {/* Local search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Find a tag…"
          value={tagSearch}
          onChange={e => setTagSearch(e.target.value)}
          className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
        />
        {tagSearch && (
          <button
            onClick={() => setTagSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Clear search"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {visibleTags.length === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500 py-1">No tags match "{tagSearch}"</p>
      ) : (
        <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
          {visibleTags.map((tag) => (
            <button
              key={tag.tagName}
              onClick={() => onTagSelect && onTagSelect(tag.tagName)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedSet.has(tag.tagName)
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border border-primary-200 dark:border-primary-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
              }`}
            >
              {tag.tagName} <span className="opacity-60">({tag.count || 0})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagsManager;
